import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import {
  ConnectionHealthRecord,
  ConnectionHealthStatus,
  getAllConnectionHealth,
  getOverallConnectionHealth,
} from "_shared/_stores/realtime/connection-health-redux-slice";
import React from "react";
import { useSelector } from "react-redux";

const StatusIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  margin: theme.spacing(0.5),
}));

const StatusPopover = styled(Popover)(({ theme }) => ({
  "& .MuiPaper-root": {
    backdropFilter: "blur(10px) brightness(105%)",
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    minWidth: theme.spacing(34),
  },
  zIndex: theme.zIndex.modal + 1,
}));

const StatusList = styled(List)(({ theme }) => ({
  padding: theme.spacing(1, 0),
}));

const StatusHeader = styled("div")(({ theme }) => ({
  padding: theme.spacing(1.5, 2, 0.5),
}));

const statusLabel: Record<ConnectionHealthStatus, string> = {
  connected: "Connected",
  connecting: "Connecting",
  disconnected: "Disconnected",
  reconnecting: "Reconnecting",
  stopped: "Stopped",
};

const severityRank: Record<ConnectionHealthStatus, number> = {
  disconnected: 0,
  reconnecting: 1,
  connecting: 2,
  connected: 3,
  stopped: 4,
};

function getStatusColor(status: ConnectionHealthStatus) {
  if (status === "disconnected") return "error.main";
  if (status === "stopped") return "error.main";
  if (status === "reconnecting") return "warning.main";
  if (status === "connected") return "success.main";
  return "text.disabled";
}

function sortConnections(connections: ConnectionHealthRecord[]) {
  return [...connections].sort((left, right) => {
    const severityDifference = severityRank[left.status] - severityRank[right.status];
    if (severityDifference !== 0) return severityDifference;
    return left.label.localeCompare(right.label);
  });
}

function getOverallStatusBadgeColor(overallStatus: ReturnType<typeof getOverallConnectionHealth>) {
  if (overallStatus === "disconnected") return "error" as const;
  if (overallStatus === "stopped") return "error" as const;
  if (overallStatus === "reconnecting" || overallStatus === "connecting") return "warning" as const;
  return "success" as const;
}

function getTooltipTitle(overallStatus: ReturnType<typeof getOverallConnectionHealth>) {
  if (overallStatus === "disconnected") {
    return "Lost connection to AttorneyHub";
  }
  if (overallStatus === "stopped") {
    return "Connection to AttorneyHub is stopped";
  }
  if (overallStatus === "reconnecting") {
    return "Reconnecting to AttorneyHub";
  }
  if (overallStatus === "connecting") {
    return "Connecting to AttorneyHub";
  }
  return "Connection to AttorneyHub is healthy";
}

function renderStatusDetail(status: ConnectionHealthStatus) {
  return (
    <Box component="span" sx={{ alignItems: "center", display: "inline-flex", gap: 0.75 }}>
      <span>{statusLabel[status]}</span>
      {status === "reconnecting" ? <CircularProgress size={10} thickness={6} /> : null}
    </Box>
  );
}

export default function ConnectionHealthIndicator() {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const connections = useSelector(getAllConnectionHealth);
  const overallStatus = useSelector(getOverallConnectionHealth);

  const sortedConnections = sortConnections(connections);
  const issueCount = sortedConnections.filter(
    (connection) =>
      connection.status === "stopped" ||
      connection.status === "reconnecting" ||
      connection.status === "disconnected",
  ).length;
  const tooltipTitle = getTooltipTitle(overallStatus);
  const issueLabel = issueCount === 1 ? "issue" : "issues";
  const issueSummary = issueCount > 0 ? `${issueCount} ${issueLabel} detected` : "All connections healthy";

  return (
    <React.Fragment>
      <Tooltip placement="bottom" title={tooltipTitle}>
        <StatusIconButton
          aria-label="open connection status"
          onClick={(event) => setAnchorEl(anchorEl ? null : event.currentTarget)}
          size="large"
        >
          <Badge color={getOverallStatusBadgeColor(overallStatus)} variant="dot">
            <HubOutlinedIcon />
          </Badge>
        </StatusIconButton>
      </Tooltip>
      <StatusPopover
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <StatusHeader>
          <Typography variant="subtitle2">Connection status</Typography>
          <Typography color="text.secondary" variant="body2">
            {issueSummary}
          </Typography>
        </StatusHeader>
        <StatusList dense>
          {sortedConnections.map((connection) => (
            <ListItem key={connection.key}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <FiberManualRecordIcon sx={{ color: getStatusColor(connection.status), fontSize: 12 }} />
              </ListItemIcon>
              <ListItemText
                primary={connection.label}
                secondary={renderStatusDetail(connection.status)}
              />
            </ListItem>
          ))}
        </StatusList>
      </StatusPopover>
    </React.Fragment>
  );
}
